import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/layout/SiteHeader';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import config from '../config/env';
import CategoryLucideIcon from '../components/categories/CategoryLucideIcon';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${config.apiUrl || 'http://localhost:5000'}/api/categories`);
        const data = await response.json();

        if (data.success) {
          setCategories(data.data.categories || []);
        } else {
          setError('Failed to fetch categories');
        }
      } catch (err) {
        setError('Failed to connect to server');
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return (
    <SiteLayout className="bg-gray-50">
      <SiteHeader />

      <SiteMain>
        <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Categories' }]} />

        <section className="bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 py-10 md:py-12 max-w-6xl">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 tracking-tight">
              Browse by category
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl leading-relaxed">
              Find concerts, sports, tech, food, and more across Kenya.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12 md:py-16 w-full max-w-6xl">
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-11 animate-pulse">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-200 mb-5" />
                  <div className="w-10 h-[3px] bg-gray-200 mb-4" />
                  <div className="h-3 bg-gray-200 w-20" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 max-w-md mx-auto">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && categories.length === 0 && (
            <div className="text-center py-16">
              <h3 className="text-base font-medium text-gray-800 mb-1">No categories yet</h3>
              <p className="text-sm text-gray-500">Categories will appear here once they&apos;re added.</p>
            </div>
          )}

          {!loading && !error && categories.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-11 md:gap-y-14">
              {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/events?category=${category.id}`}
                    className="group flex flex-col items-center text-center px-2 py-1"
                  >
                    <CategoryLucideIcon
                      name={category.name}
                      size={40}
                      strokeWidth={1.5}
                      className="mb-5 text-gray-700 transition-all duration-300 group-hover:scale-105 group-hover:text-orange-600"
                    />
                    <span className="block w-10 h-[3px] mb-4 bg-gray-300 transition-all duration-300 group-hover:w-14 group-hover:bg-orange-500" />
                    <span className="text-[13px] font-medium leading-snug max-w-[9.5rem] text-gray-800 transition-colors duration-200 group-hover:text-orange-600">
                      {category.name}
                    </span>
                  </Link>
              ))}
            </div>
          )}
        </div>
      </SiteMain>
    </SiteLayout>
  );
};

export default CategoriesPage;
